import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { vi } from "date-fns/locale";
import {
  AlertTriangle,
  ChevronRight,
  FileText,
  FolderOpen,
  Inbox,
  PhoneCall,
  RefreshCw,
  UserPlus,
  Users,
} from "lucide-react";
import { hasPermission } from "../../utils/permissionUtils";
import { getCrmDashboard } from "../../api/crm/dashboard";
import {
  getAllCustomers,
  getCustomerStats,
} from "../../api/crm/customers/customerInfo";
import {
  getAllContactHistories,
  getContactHistoryStats,
} from "../../api/crm/contact-history/info";
import { getAllContracts } from "../../api/crm/contracts/info";
import { getAllContractDocuments } from "../../api/crm/contract-documents/info";
import { getSortedCrmHubItems, HUB_PREVIEW_LIMIT } from "./mobileNavConfig";
import {
  MobilePageShell,
  MobilePageHeader,
  MobileSectionHeader,
  MobileNavGroup,
  MobileNavRow,
} from "./MobileNavPrimitives";

const CRM_ITEM_TONES = {
  customers: "sky",
  contact_history: "amber",
  contracts: "emerald",
  documents: "indigo",
  teacher_accounts: "violet",
  dashboard: "slate",
  events: "teal",
  calendar: "rose",
};

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function getFollowupMeta(dateStr) {
  if (!dateStr) return null;
  const target = startOfDay(dateStr);
  const today = startOfDay(new Date());
  const diff = Math.round((target - today) / 86400000);
  if (diff < 0) {
    return {
      short: `Quá hạn ${Math.abs(diff)}n`,
      tone: "overdue",
      chip: "bg-rose-50 text-rose-700 border-rose-100",
      rank: 0,
    };
  }
  if (diff === 0) {
    return {
      short: "Hôm nay",
      tone: "today",
      chip: "bg-amber-50 text-amber-800 border-amber-100",
      rank: 1,
    };
  }
  if (diff === 1) {
    return {
      short: "Ngày mai",
      tone: "soon",
      chip: "bg-orange-50 text-orange-700 border-orange-100",
      rank: 2,
    };
  }
  return {
    short: format(target, "dd/MM"),
    tone: "ok",
    chip: "bg-slate-50 text-slate-600 border-slate-100",
    rank: 3,
  };
}

function formatShortDate(value) {
  if (!value) return "—";
  try {
    return format(new Date(value), "dd/MM", { locale: vi });
  } catch {
    return "—";
  }
}

function JournalSection({ title, tone, action, children }) {
  return (
    <section>
      <MobileSectionHeader title={title} tone={tone} action={action} />
      {children}
    </section>
  );
}

function JournalList({ items, emptyText, renderItem }) {
  if (!items.length) {
    return (
      <p className="rounded-2xl border border-dashed border-gray-200 bg-white px-4 py-8 text-center text-[12px] text-gray-400">
        {emptyText}
      </p>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm divide-y divide-gray-50">
      {items.map(renderItem)}
    </div>
  );
}

function StatCard({ label, value, hint, icon: Icon, tone, onClick }) {
  const tones = {
    sky: "bg-sky-50 border-sky-100 text-sky-700",
    amber: "bg-amber-50 border-amber-100 text-amber-700",
    emerald: "bg-emerald-50 border-emerald-100 text-emerald-700",
    rose: "bg-rose-50 border-rose-100 text-rose-700",
    violet: "bg-violet-50 border-violet-100 text-violet-700",
  };
  const t = tones[tone] || tones.sky;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex min-h-[100px] flex-col rounded-2xl border p-3.5 text-left shadow-sm active:scale-[0.98] transition-transform ${t}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/70">
          <Icon size={16} />
        </div>
        <ChevronRight size={14} className="opacity-40" />
      </div>
      <p className="mt-2.5 text-[11px] font-semibold opacity-80 leading-snug">
        {label}
      </p>
      <p className="mt-0.5 text-2xl font-black tabular-nums text-gray-900">
        {value}
      </p>
      {hint ? (
        <p className="mt-1 text-[10px] font-medium text-gray-500/90 line-clamp-1">
          {hint}
        </p>
      ) : (
        <span className="mt-1 block h-3.5" aria-hidden />
      )}
    </button>
  );
}

export default function CrmHubScreen() {
  const navigate = useNavigate();
  const items = useMemo(() => getSortedCrmHubItems(), []);

  const canCrmDash = hasPermission("crm.dashboard", "R");
  const canCustomers = hasPermission("crm.customers", "R");
  const canContactHistory = hasPermission("crm.contact_history", "R");
  const canContracts = hasPermission("crm.contracts", "R");
  const canDocuments = hasPermission("crm.documents", "R");

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState({
    customersTotal: null,
    customersNew: null,
    contractsActive: null,
    contractsExpiring: null,
    contactsMonth: null,
    followups: null,
    phoneContacts: null,
    scopeLabel: "",
  });
  const [statusBreakdown, setStatusBreakdown] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [followupCounts, setFollowupCounts] = useState({
    overdue: 0,
    today: 0,
    soon: 0,
  });
  const [recentCustomers, setRecentCustomers] = useState([]);
  const [recentContracts, setRecentContracts] = useState([]);
  const [recentDocuments, setRecentDocuments] = useState([]);

  const load = useCallback(
    async (isRefresh = false) => {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);

      const nextStats = {
        customersTotal: null,
        customersNew: null,
        contractsActive: null,
        contractsExpiring: null,
        contactsMonth: null,
        followups: null,
        phoneContacts: null,
        scopeLabel: "",
      };
      let nextBreakdown = [];
      let nextRecent = [];
      let nextFollowups = [];
      let nextFollowupCounts = { overdue: 0, today: 0, soon: 0 };
      let nextCustomers = [];
      let nextContracts = [];
      let nextDocuments = [];

      const jobs = [];

      if (canCrmDash) {
        jobs.push(
          getCrmDashboard({
            year: new Date().getFullYear(),
            month: new Date().getMonth() + 1,
            branch_id: "all",
          })
            .then((data) => {
              const s = data?.stats;
              if (s) {
                nextStats.customersTotal = Number(s.customers?.total ?? 0);
                nextStats.customersNew = Number(s.customers?.newThisMonth ?? 0);
                nextStats.contractsActive = Number(s.contracts?.active ?? 0);
                nextStats.contractsExpiring = Number(
                  s.contracts?.expiringSoon ?? 0
                );
                nextStats.contactsMonth = Number(s.contacts?.thisMonth ?? 0);
                nextStats.followups = Number(
                  s.contacts?.upcomingFollowups ?? 0
                );
              }
              nextStats.scopeLabel = data?.meta?.scopeLabel || "";
              nextBreakdown = Array.isArray(data?.charts?.customerStatus)
                ? data.charts.customerStatus.slice(0, HUB_PREVIEW_LIMIT)
                : [];
              nextRecent = Array.isArray(data?.recentActivities)
                ? data.recentActivities.slice(0, HUB_PREVIEW_LIMIT)
                : [];
            })
            .catch(() => {})
        );
      } else {
        if (canCustomers) {
          jobs.push(
            getCustomerStats({})
              .then((data) => {
                nextStats.customersTotal = Number(data?.total ?? 0);
                nextStats.customersNew = Number(data?.new_count ?? 0);
              })
              .catch(() => {})
          );
        }
        if (canContactHistory) {
          jobs.push(
            getContactHistoryStats({})
              .then((data) => {
                nextStats.followups = Number(data?.upcoming_followups ?? 0);
                nextStats.contactsMonth = Number(
                  data?.due_today_followups ??
                    data?.today_contacts ??
                    data?.this_month_contacts ??
                    0
                );
                nextStats.phoneContacts = Number(data?.phone_contacts ?? 0);
              })
              .catch(() => {})
          );
        }
      }

      if (canContactHistory) {
        jobs.push(
          getAllContactHistories({
            has_followup: "1",
            page: 1,
            pageSize: 40,
          })
            .then((data) => {
              const itemsList = Array.isArray(data?.items) ? data.items : [];
              const ranked = itemsList
                .map((item) => ({
                  ...item,
                  _meta: getFollowupMeta(item.next_contact_date),
                }))
                .filter((item) => item._meta)
                .sort((a, b) => {
                  const rd = (a._meta.rank ?? 9) - (b._meta.rank ?? 9);
                  if (rd !== 0) return rd;
                  const at = a.next_contact_date
                    ? new Date(a.next_contact_date).getTime()
                    : 0;
                  const bt = b.next_contact_date
                    ? new Date(b.next_contact_date).getTime()
                    : 0;
                  return at - bt;
                });

              nextFollowupCounts = {
                overdue: ranked.filter((x) => x._meta.tone === "overdue")
                  .length,
                today: ranked.filter((x) => x._meta.tone === "today").length,
                soon: ranked.filter(
                  (x) => x._meta.tone === "soon" || x._meta.tone === "ok"
                ).length,
              };
              nextFollowups = ranked.slice(0, HUB_PREVIEW_LIMIT);
            })
            .catch(() => {})
        );

        if (!canCrmDash) {
          jobs.push(
            getAllContactHistories({
              page: 1,
              pageSize: HUB_PREVIEW_LIMIT,
            })
              .then((data) => {
                nextRecent = Array.isArray(data?.items)
                  ? data.items.slice(0, HUB_PREVIEW_LIMIT)
                  : [];
              })
              .catch(() => {})
          );
        }
      }

      if (canCustomers) {
        jobs.push(
          getAllCustomers({ page: 1, pageSize: HUB_PREVIEW_LIMIT })
            .then((data) => {
              nextCustomers = Array.isArray(data?.items)
                ? data.items.slice(0, HUB_PREVIEW_LIMIT)
                : [];
            })
            .catch(() => {})
        );
      }

      if (canContracts) {
        jobs.push(
          getAllContracts({ limit: HUB_PREVIEW_LIMIT })
            .then((data) => {
              nextContracts = Array.isArray(data?.items)
                ? data.items.slice(0, HUB_PREVIEW_LIMIT)
                : [];
            })
            .catch(() => {})
        );
      }

      if (canDocuments) {
        jobs.push(
          getAllContractDocuments({ page: 1, pageSize: HUB_PREVIEW_LIMIT })
            .then((data) => {
              nextDocuments = Array.isArray(data?.items)
                ? data.items.slice(0, HUB_PREVIEW_LIMIT)
                : [];
            })
            .catch(() => {})
        );
      }

      await Promise.all(jobs);
      setStats(nextStats);
      setStatusBreakdown(nextBreakdown);
      setRecentActivities(nextRecent);
      setFollowups(nextFollowups);
      setFollowupCounts(nextFollowupCounts);
      setRecentCustomers(nextCustomers);
      setRecentContracts(nextContracts);
      setRecentDocuments(nextDocuments);
      setLoading(false);
      setRefreshing(false);
    },
    [canCrmDash, canCustomers, canContactHistory, canContracts, canDocuments]
  );

  useEffect(() => {
    load(false);
  }, [load]);

  const kpiCards = useMemo(() => {
    const list = [];
    if (stats.customersTotal != null && (canCrmDash || canCustomers)) {
      list.push({
        key: "customers",
        label: "Tổng khách hàng",
        value: stats.customersTotal,
        hint:
          stats.customersNew != null
            ? `+${stats.customersNew} mới trong tháng`
            : null,
        icon: Users,
        tone: "sky",
        href: "/crm/customers",
      });
    }
    if (stats.followups != null && (canCrmDash || canContactHistory)) {
      list.push({
        key: "followups",
        label: "Follow-up sắp tới",
        value: stats.followups,
        hint:
          followupCounts.overdue > 0
            ? `${followupCounts.overdue} quá hạn`
            : followupCounts.today > 0
              ? `${followupCounts.today} hẹn hôm nay`
              : "Lịch chăm sóc",
        icon: PhoneCall,
        tone: "amber",
        href: "/m/crm/followups",
      });
    }
    if (
      stats.contractsActive != null &&
      (canCrmDash || canContracts)
    ) {
      list.push({
        key: "contracts",
        label: "HĐ đang thực hiện",
        value: stats.contractsActive,
        hint:
          stats.contractsExpiring != null
            ? `${stats.contractsExpiring} sắp hết hạn 30 ngày`
            : null,
        icon: FileText,
        tone: "emerald",
        href: "/crm/contracts",
      });
    }
    if (stats.contactsMonth != null && (canCrmDash || canContactHistory)) {
      list.push({
        key: "contactsMonth",
        label: "Lịch sử liên hệ",
        value: stats.contactsMonth,
        hint:
          stats.phoneContacts != null
            ? `${stats.phoneContacts} cuộc gọi`
            : "Tương tác gần đây",
        icon: PhoneCall,
        tone: "rose",
        href: "/crm/contact_history",
      });
    }
    if (
      stats.contractsExpiring != null &&
      list.length < 4 &&
      (canCrmDash || canContracts)
    ) {
      list.push({
        key: "expiring",
        label: "Sắp hết hạn 30 ngày",
        value: stats.contractsExpiring,
        hint: "Cần rà soát gia hạn",
        icon: AlertTriangle,
        tone: "violet",
        href: "/crm/contracts",
      });
    }
    return list.slice(0, 4);
  }, [
    stats,
    followupCounts,
    canCrmDash,
    canCustomers,
    canContactHistory,
    canContracts,
  ]);

  const monthLabel = format(new Date(), "MMMM yyyy", { locale: vi });
  const showJournalBlock =
    canContactHistory || canCustomers || canContracts || canDocuments;
  const hasJournalContent =
    recentActivities.length > 0 ||
    recentCustomers.length > 0 ||
    recentContracts.length > 0 ||
    recentDocuments.length > 0;

  return (
    <MobilePageShell>
      <MobilePageHeader
        title="CRM"
        subtitle={
          stats.scopeLabel
            ? `${stats.scopeLabel} · ${monthLabel}`
            : `Khách hàng & chăm sóc · ${monthLabel}`
        }
        tone="sky"
        trailing={
          <button
            type="button"
            onClick={() => load(true)}
            disabled={refreshing || loading}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-sky-100 bg-sky-50 text-sky-700 active:bg-sky-100 disabled:opacity-50"
            aria-label="Làm mới"
          >
            <RefreshCw
              size={16}
              className={refreshing ? "animate-spin" : undefined}
            />
          </button>
        }
      />

      <div className="space-y-5 px-5 pt-5 pb-4">
        {(canCrmDash || canCustomers || canContactHistory || canContracts) && (
          <section>
            <MobileSectionHeader title="Tình hình CRM" tone="sky" />
            {loading ? (
              <div className="grid grid-cols-2 gap-3">
                {[0, 1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="h-[100px] animate-pulse rounded-2xl bg-sky-100/50"
                  />
                ))}
              </div>
            ) : kpiCards.length > 0 ? (
              <div className="grid grid-cols-2 gap-3">
                {kpiCards.map((card) => (
                  <StatCard
                    key={card.key}
                    label={card.label}
                    value={card.value}
                    hint={card.hint}
                    icon={card.icon}
                    tone={card.tone}
                    onClick={() => navigate(card.href)}
                  />
                ))}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-sky-200 bg-sky-50/40 px-4 py-8 text-center text-[12px] text-gray-400">
                Chưa có chỉ số CRM
              </div>
            )}
          </section>
        )}

        {canCrmDash && statusBreakdown.length > 0 ? (
          <section>
            <MobileSectionHeader title="Trạng thái khách hàng" tone="sky" />
            <div className="flex flex-wrap gap-2 rounded-2xl border border-sky-100 bg-white p-3.5 shadow-sm">
              {statusBreakdown.map((row, idx) => {
                const name =
                  row.label || row.name || row.status || `Nhóm ${idx + 1}`;
                const value = Number(row.value ?? row.count ?? 0);
                return (
                  <button
                    key={`${name}-${idx}`}
                    type="button"
                    onClick={() => navigate("/crm/customers")}
                    className="inline-flex items-center gap-1.5 rounded-full border border-sky-100 bg-sky-50 px-3 py-1.5 text-[11px] font-semibold text-sky-800 active:bg-sky-100"
                  >
                    <span className="max-w-[9rem] truncate">{name}</span>
                    <span className="tabular-nums text-sky-600">{value}</span>
                  </button>
                );
              })}
            </div>
          </section>
        ) : null}

        {!loading && canContactHistory && followups.length > 0 ? (
          <section className="space-y-3">
            <JournalSection
              title="Cần bạn xử lý"
              tone="rose"
              action={
                <button
                  type="button"
                  onClick={() => navigate("/m/crm/followups")}
                  className="text-[12px] font-medium text-rose-700"
                >
                  Xem tất cả
                </button>
              }
            >
              {followupCounts.overdue > 0 ||
              followupCounts.today > 0 ||
              followupCounts.soon > 0 ? (
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  {followupCounts.overdue > 0 ? (
                    <span className="rounded-full border border-rose-100 bg-rose-50 px-2.5 py-1 text-[10px] font-semibold text-rose-700">
                      {followupCounts.overdue} quá hạn
                    </span>
                  ) : null}
                  {followupCounts.today > 0 ? (
                    <span className="rounded-full border border-amber-100 bg-amber-50 px-2.5 py-1 text-[10px] font-semibold text-amber-800">
                      {followupCounts.today} hôm nay
                    </span>
                  ) : null}
                  {followupCounts.soon > 0 ? (
                    <span className="rounded-full border border-sky-100 bg-sky-50 px-2.5 py-1 text-[10px] font-semibold text-sky-700">
                      {followupCounts.soon} sắp tới
                    </span>
                  ) : null}
                </div>
              ) : null}
              <JournalList
                items={followups}
                emptyText="Không có lịch follow-up cần ưu tiên"
                renderItem={(item) => (
                  <button
                    key={
                      item.id ||
                      `${item.customer_id}-${item.next_contact_date}`
                    }
                    type="button"
                    onClick={() => navigate("/m/crm/followups")}
                    className="flex w-full items-start gap-3 px-4 py-3.5 text-left active:bg-gray-50"
                  >
                    <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-rose-50 text-rose-700">
                      <PhoneCall size={16} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-800">
                        {item.customer_name || "Khách hàng"}
                      </p>
                      <p className="mt-0.5 truncate text-[11px] text-gray-400">
                        {[item.branch_name, item.method]
                          .filter(Boolean)
                          .join(" · ") || "Follow-up"}
                      </p>
                      <span
                        className={`mt-1.5 inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold ${item._meta.chip}`}
                      >
                        {item._meta.short}
                      </span>
                    </div>
                    <ChevronRight
                      size={14}
                      className="mt-2 shrink-0 text-gray-300"
                    />
                  </button>
                )}
              />
            </JournalSection>
          </section>
        ) : null}

        <section>
          <MobileSectionHeader title="Lối tắt" tone="indigo" />
          {items.length > 0 ? (
            <MobileNavGroup>
              {items.map((item) => (
                <MobileNavRow
                  key={item.key}
                  icon={item.icon}
                  label={item.label}
                  tone={CRM_ITEM_TONES[item.key] || "sky"}
                  onClick={() => navigate(`/crm/${item.key}`)}
                />
              ))}
            </MobileNavGroup>
          ) : (
            <div className="rounded-2xl border border-dashed border-sky-200 bg-sky-50/40 px-4 py-10 text-center">
              <Inbox className="mx-auto mb-3 text-sky-300" size={26} />
              <p className="text-[12px] text-gray-400">
                Không có chức năng CRM khả dụng
              </p>
            </div>
          )}
        </section>

        {showJournalBlock ? (
          <section className="space-y-5">
            <MobileSectionHeader title="Nhật ký gần nhất" tone="amber" />
            <p className="-mt-1 pl-3 text-[11px] text-gray-500">
              Liên hệ, khách hàng và hợp đồng theo quyền của bạn
            </p>

            {loading ? (
              <div className="space-y-3">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="h-28 animate-pulse rounded-2xl bg-amber-100/40"
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-5">
                {canContactHistory || canCrmDash ? (
                  <JournalSection
                    title="Nhật ký liên hệ gần nhất"
                    tone="emerald"
                    action={
                      <button
                        type="button"
                        onClick={() => navigate("/crm/contact_history")}
                        className="text-[12px] font-medium text-emerald-700"
                      >
                        Xem tất cả
                      </button>
                    }
                  >
                    <JournalList
                      items={recentActivities}
                      emptyText="Chưa có hoạt động liên hệ gần đây"
                      renderItem={(item) => (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => navigate("/crm/contact_history")}
                          className="flex w-full items-start gap-3 px-4 py-3.5 text-left active:bg-gray-50"
                        >
                          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
                            <PhoneCall size={16} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-gray-800">
                              {item.customer_name || "—"}
                            </p>
                            <p className="mt-0.5 truncate text-[11px] text-gray-400">
                              {[
                                item.method,
                                formatShortDate(item.contact_date),
                                item.employee_name,
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </p>
                            {item.next_contact_date ? (
                              <p className="mt-1 text-[10px] font-medium text-emerald-700/80">
                                Follow-up:{" "}
                                {formatShortDate(item.next_contact_date)}
                              </p>
                            ) : null}
                          </div>
                          <ChevronRight
                            size={14}
                            className="mt-2 shrink-0 text-gray-300"
                          />
                        </button>
                      )}
                    />
                  </JournalSection>
                ) : null}

                {canCustomers ? (
                  <JournalSection
                    title="Nhật ký khách hàng mới"
                    tone="sky"
                    action={
                      <button
                        type="button"
                        onClick={() => navigate("/crm/customers")}
                        className="text-[12px] font-medium text-sky-700"
                      >
                        Danh sách
                      </button>
                    }
                  >
                    <JournalList
                      items={recentCustomers}
                      emptyText="Chưa có khách hàng mới gần đây"
                      renderItem={(item) => (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => navigate("/crm/customers")}
                          className="flex w-full items-start gap-3 px-4 py-3.5 text-left active:bg-gray-50"
                        >
                          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sky-700">
                            <UserPlus size={16} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-gray-800">
                              {item.name || item.customer_name || "Khách hàng"}
                            </p>
                            <p className="mt-0.5 truncate text-[11px] text-gray-400">
                              {[
                                item.status,
                                item.branch_name,
                                item.phone || item.mobile,
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </p>
                          </div>
                          <span className="mt-1 shrink-0 text-[10px] font-medium text-gray-400">
                            {formatShortDate(item.created_at)}
                          </span>
                        </button>
                      )}
                    />
                  </JournalSection>
                ) : null}

                {canContracts ? (
                  <JournalSection
                    title="Nhật ký hợp đồng gần nhất"
                    tone="violet"
                    action={
                      <button
                        type="button"
                        onClick={() => navigate("/crm/contracts")}
                        className="text-[12px] font-medium text-violet-700"
                      >
                        Xem thêm
                      </button>
                    }
                  >
                    <JournalList
                      items={recentContracts}
                      emptyText="Chưa có hợp đồng gần đây"
                      renderItem={(item) => (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => navigate("/crm/contracts")}
                          className="flex w-full items-start gap-3 px-4 py-3.5 text-left active:bg-gray-50"
                        >
                          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-violet-50 text-violet-700">
                            <FileText size={16} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-gray-800">
                              {item.contract_code ||
                                item.code ||
                                item.name ||
                                "Hợp đồng"}
                            </p>
                            <p className="mt-0.5 truncate text-[11px] text-gray-400">
                              {[
                                item.customer_name,
                                item.status,
                                item.branch_name,
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </p>
                          </div>
                          <span className="mt-1 shrink-0 text-[10px] font-medium text-gray-400">
                            {formatShortDate(
                              item.signed_date || item.created_at
                            )}
                          </span>
                        </button>
                      )}
                    />
                  </JournalSection>
                ) : null}

                {canDocuments ? (
                  <JournalSection
                    title="Nhật ký hồ sơ liên kết"
                    tone="indigo"
                    action={
                      <button
                        type="button"
                        onClick={() => navigate("/crm/documents")}
                        className="text-[12px] font-medium text-indigo-700"
                      >
                        Hồ sơ
                      </button>
                    }
                  >
                    <JournalList
                      items={recentDocuments}
                      emptyText="Chưa có hồ sơ liên kết gần đây"
                      renderItem={(item) => (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => navigate("/crm/documents")}
                          className="flex w-full items-start gap-3 px-4 py-3.5 text-left active:bg-gray-50"
                        >
                          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-700">
                            <FolderOpen size={16} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-gray-800">
                              {item.file_name ||
                                item.document_name ||
                                item.name ||
                                "Hồ sơ"}
                            </p>
                            <p className="mt-0.5 truncate text-[11px] text-gray-400">
                              {[
                                item.contract_code || item.customer_name,
                                item.doc_type || item.document_type,
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </p>
                          </div>
                          <span className="mt-1 shrink-0 text-[10px] font-medium text-gray-400">
                            {formatShortDate(item.created_at)}
                          </span>
                        </button>
                      )}
                    />
                  </JournalSection>
                ) : null}

                {!hasJournalContent ? (
                  <p className="rounded-2xl border border-dashed border-amber-200 bg-amber-50/40 px-4 py-8 text-center text-[12px] text-gray-400">
                    Chưa có nhật ký để hiển thị theo quyền hiện tại
                  </p>
                ) : null}
              </div>
            )}
          </section>
        ) : null}
      </div>
    </MobilePageShell>
  );
}
